import React, { useMemo } from 'react';
import './BettingDistribution.css';
import { Pie, PieChart, ResponsiveContainer, Cell } from 'recharts';
import { Race } from '../../../../types/race';
import { useEnvAddresses } from '../../../../hooks/useEnvAddresses';
import { formatUnits } from 'viem';
import { calculateRewardMultiplier } from '../../../../utils/calculateRewardMultiplier';
import { formatTokenAmount } from '../../../../utils/formatTokenAmount';

const TOKEN_COLORS: Record<string, string> = {
  BTC: '#F7931A',
  ETH: '#627EEA',
  XRP: '#23292F',
  SOL: '#14F195',
  DOT: '#E6007A',
  ADA: '#0033AD',
  AVAX: '#E84142',
  LINK: '#2A5ADA',
  MATIC: '#8247E5',
  UNI: '#FF007A',
};

const getTokenColor = (tokenSymbol: string) => {
  if (!tokenSymbol) return '#6C5DD3';
  const upperSymbol = tokenSymbol.toUpperCase();
  return (
    TOKEN_COLORS[upperSymbol] ||
    `hsl(${Math.abs(
      upperSymbol.split('').reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0) % 360
    )}, 70%, 45%)`
  );
};

type BettingDistributionProps = {
  race: Race;
};

const BettingDistribution = ({ race }: BettingDistributionProps) => {
  const { usdc } = useEnvAddresses();
  const pieData = useMemo(() => {
    return race.tokenStats.map(tokenStats => ({
      name: tokenStats.token.symbol,
      value: Number(formatTokenAmount(tokenStats.totalBetsAmount, usdc.decimals, 'usdc')),
      rewardMultiplier: calculateRewardMultiplier(
        Number(formatUnits((race.totalPool * (BigInt(1e18) - race.creatorFeePercent)) / BigInt(1e18), usdc.decimals)),
        Number(
          formatUnits(
            (tokenStats.totalBetsAmount * (BigInt(1e18) - race.creatorFeePercent)) / BigInt(1e18),
            usdc.decimals
          )
        )
      ),
      color: getTokenColor(tokenStats.token.symbol),
    }));
  }, [race, usdc.decimals]);

  return (
    <div className="betting-distribution flex items-center">
      <div className="distribution-chart h-52 w-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              innerRadius="60%"
              outerRadius="100%"
              dataKey="value"
              cornerRadius={4}
              stroke="transparent"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div>
        {pieData.map(entry => (
          <div key={entry.name} className="legend-item">
            <div className="legend-symbol">
              <div className="color-indicator" style={{ backgroundColor: entry.color }} />
              <span>{entry.name}</span>
            </div>
            <span className="bet-percentage">{entry.rewardMultiplier}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BettingDistribution;
